/**
 * GatewayCapacityCard (OCP-567) - "do we have to scale the AI gateway, and on
 * which dimension?" - operator-side.
 *
 * Sibling of GatewayHealthCard (OCP-555): where that one answers "is the
 * gateway up right now?", this one is the early warning the 2026-08-11 outage
 * (OCP-566) lacked - the host sat at 84% memory while serving 0.1% of its call
 * capacity, so a calls-only view would have been green. The card shows the
 * five dimensions (memory / cpu / throughput / upstream / backend - the
 * vLLM model host, OCP-622) as bars against the
 * alert bar, names the BINDING one, the tenant headroom and the scaling action
 * that applies to that dimension, plus a strip of the recent readings.
 *
 * Self-fetching for the same reason as the health card: a capacity-read
 * failure must never blank the model table.
 */
import { useCallback, useEffect, useState } from 'react';

import { api, ApiError } from '../lib/api';
import { fmtDateTime } from '../lib/format-time';

type Dimension = 'memory' | 'cpu' | 'throughput' | 'upstream' | 'backend';
type Binding = Dimension | 'none';

interface DimensionReading {
  pct: number | null;
  detail: string;
  unavailable: string | null;
}

interface CapacityReading {
  evaluated_at: string;
  window_minutes: number;
  level: 'ok' | 'warning';
  binding: Binding;
  binding_pct: number | null;
  reason: string;
  dimensions: Record<Dimension, DimensionReading>;
  headroom: {
    wired_tenants: number;
    per_tenant_rpm: number | null;
    more_tenants: number | null;
    detail: string;
  };
  scaling_action: string;
  inputs: {
    host: { server_id: string | null; hostname: string | null; snapshot_at: string | null };
    /** OCP-622 - the model host (null before it joined the fleet / in readings that predate it). */
    backend?: { server_id: string; hostname: string | null; gpu_vram_pct: number | null; vllm: { waiting: number; running: number; kv_cache_pct: number | null } | null; vllm_unavailable: string | null } | null;
    traffic: { requests: number; requests_per_min: number | null; latency_p95_ms: number | null } | null;
    config: { alertPct: number; recoverPct: number; workers: number; rpsPerWorker: number };
  };
}

interface HistoryRow {
  evaluated_at: string;
  level: 'ok' | 'warning';
  binding: Binding;
  binding_pct: number | null;
}

interface CapacityResponse {
  current: CapacityReading | null;
  alert: {
    alerting: boolean;
    since: string | null;
    last_alerted_at: string | null;
    last_reason: string | null;
    last_binding: string | null;
  };
  history: HistoryRow[];
}

/** 4 hours at one reading per 5 minutes. */
const STRIP_SAMPLES = 48;

const DIMENSIONS: Array<{ key: Dimension; label: string }> = [
  { key: 'memory', label: 'Memory' },
  { key: 'cpu', label: 'CPU' },
  { key: 'throughput', label: 'Throughput' },
  { key: 'upstream', label: 'Upstream' },
  { key: 'backend', label: 'Backend (model host)' },
];

export const BINDING_LABEL: Record<Binding, string> = {
  memory: 'Memory-bound',
  cpu: 'CPU-bound',
  throughput: 'Throughput-bound',
  upstream: 'Upstream-bound',
  backend: 'Backend-bound',
  none: 'Not measurable',
};

function barClasses(pct: number | null, alertPct: number): string {
  if (pct === null) return 'bg-gray-300 dark:bg-gray-700';
  if (pct >= alertPct) return 'bg-red-500';
  if (pct >= alertPct * 0.75) return 'bg-amber-500';
  return 'bg-green-500/80';
}

function stripClasses(row: HistoryRow, alertPct: number): string {
  if (row.binding_pct === null) return 'bg-gray-300 dark:bg-gray-700';
  if (row.level === 'warning') return 'bg-red-500';
  if (row.binding_pct >= alertPct * 0.75) return 'bg-amber-500';
  return 'bg-green-500/80';
}

function relativeMinutes(iso: string): string {
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.round(hours / 24)} d ago`;
}

export function GatewayCapacityCard() {
  const [data, setData] = useState<CapacityResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setData(await api<CapacityResponse>(`/v1/admin/gateway/capacity?limit=${STRIP_SAMPLES}`));
    } catch (err) {
      setLoadError(
        err instanceof ApiError
          ? `Could not load gateway capacity (HTTP ${err.status}).`
          : 'Could not load gateway capacity.',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const current = data?.current ?? null;
  const alertPct = current?.inputs.config.alertPct ?? 80;

  return (
    <section
      data-testid="gateway-capacity-card"
      className="mt-6 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5 shadow-sm"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Gateway capacity</h2>
          <p className="mt-1 max-w-2xl text-xs text-gray-500 dark:text-gray-400">
            Every 5 minutes orboto Control reads the gateway host&apos;s memory and CPU (host-agent), the
            request rate against the worker ceiling, the upstream latency / failures (LiteLLM spend log)
            and the model host (worst GPU VRAM from its beacon, vLLM KV cache + waiting queue from its /metrics),
            and names the dimension that binds. The alert opens at {alertPct}% on any of them - request
            volume alone would not have caught the 2026-08-11 memory outage.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          disabled={loading}
          className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-xs font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {loadError && (
        <div
          role="alert"
          className="mt-4 rounded border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300"
        >
          {loadError}
        </div>
      )}

      {!loadError && data && !current && (
        <div className="mt-4 rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 px-3 py-2 text-sm text-gray-600 dark:text-gray-400">
          No capacity reading yet - the check runs every 5 minutes.
        </div>
      )}

      {!loadError && data && current && (
        <>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <span
              data-testid="gateway-capacity-binding"
              className={`inline-flex items-center rounded px-2 py-1 text-xs font-semibold uppercase tracking-wide ring-1 ring-inset ${
                current.level === 'warning'
                  ? 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 ring-red-200 dark:ring-red-800'
                  : current.binding === 'none'
                    ? 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 ring-gray-200 dark:ring-gray-700'
                    : 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 ring-green-200 dark:ring-green-800'
              }`}
            >
              {BINDING_LABEL[current.binding]}
              {current.binding_pct !== null ? ` ${current.binding_pct}%` : ''}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              evaluated {relativeMinutes(current.evaluated_at)} · {current.window_minutes} min window
              {current.inputs.host.hostname ? ` · ${current.inputs.host.hostname}` : ''}
            </span>
          </div>

          <p className="mt-2 text-sm text-gray-700 dark:text-gray-300" data-testid="gateway-capacity-reason">
            {current.reason}
          </p>

          {data.alert.alerting && (
            <div
              role="alert"
              className="mt-4 rounded border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300"
            >
              <span className="font-semibold">Capacity alert open</span>
              {data.alert.since ? ` since ${fmtDateTime(data.alert.since)}` : ''}
              {data.alert.last_binding ? ` · ${BINDING_LABEL[(data.alert.last_binding as Binding) ?? 'none'] ?? data.alert.last_binding}` : ''}.
              {' '}
              <span className="font-semibold">Action:</span> {current.scaling_action}
            </div>
          )}

          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="gateway-capacity-dimensions">
            {DIMENSIONS.map(({ key, label }) => {
              const d = current.dimensions[key] ?? { pct: null, detail: 'reading predates this dimension', unavailable: 'not evaluated in this reading' };
              const isBinding = current.binding === key;
              return (
                <div
                  key={key}
                  className={`rounded-lg border p-3 ${isBinding ? 'border-gray-400 dark:border-gray-500' : 'border-gray-200 dark:border-gray-800'} bg-gray-50 dark:bg-gray-800/40`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                      {label}
                      {isBinding ? ' · binding' : ''}
                    </span>
                    <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {d.pct === null ? 'n/a' : `${d.pct}%`}
                    </span>
                  </div>
                  <div className="mt-2 h-2 w-full overflow-hidden rounded bg-gray-200 dark:bg-gray-700" title={`alert at ${alertPct}%`}>
                    <div
                      className={`h-2 ${barClasses(d.pct, alertPct)}`}
                      style={{ width: `${Math.min(100, Math.max(0, d.pct ?? 0))}%` }}
                    />
                  </div>
                  <p className="mt-2 text-xs text-gray-600 dark:text-gray-400">{d.unavailable ?? d.detail}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatTile label="Wired tenants">{current.headroom.wired_tenants}</StatTile>
            <StatTile label="Per tenant">
              {current.headroom.per_tenant_rpm === null ? '-' : `${current.headroom.per_tenant_rpm}/min`}
            </StatTile>
            <StatTile label="More tenants">
              {current.headroom.more_tenants === null ? 'not tenant-bound' : `~${current.headroom.more_tenants}`}
            </StatTile>
            <StatTile label="Workers x rate">
              {current.inputs.config.workers} x {current.inputs.config.rpsPerWorker} req/s
            </StatTile>
          </div>

          {!data.alert.alerting && (
            <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">{current.scaling_action}</p>
          )}

          {data.history.length > 0 && (
            <div className="mt-4">
              <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Recent readings (oldest to newest)
              </div>
              <div className="mt-2 flex gap-[2px]" data-testid="gateway-capacity-strip">
                {[...data.history].reverse().map((r) => (
                  <span
                    key={r.evaluated_at}
                    title={`${fmtDateTime(r.evaluated_at)} - ${BINDING_LABEL[r.binding]}${r.binding_pct !== null ? ` ${r.binding_pct}%` : ''}`}
                    className={`h-6 flex-1 rounded-sm ${stripClasses(r, alertPct)}`}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function StatTile({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40 p-3">
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</div>
      <div className="mt-1 text-sm font-semibold text-gray-900 dark:text-gray-100">{children}</div>
    </div>
  );
}
